/**
 * FilterPanel - Left sidebar filter panel for Cells Page
 *
 * Collapsible panel with filter sections, range sliders, multi-select options
 * Uses dynamic data from filterDataService with faceted search (dynamic counts)
 */

import { createPortal } from 'preact/compat'
import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { getFilterMetadata, onFilterMetadataUpdate } from '../../modules/filterDataService.js'
import {
    activeFilterCount,
    agingRange,
    batchVariationRange,
    capacityRange,
    collapseAllSections,
    currentRange,
    dynamicFilterCounts,
    dynamicRangeData,
    energyRange,
    expandAllSections,
    // Panel state
    filterPanelCollapsed,
    // Computed
    hasActiveFilters,
    initFilterRangesFromMetadata,
    isSectionExpanded,
    powerRange,
    resetAllFilters,
    savedSearchTerms,
    searchQuery,
    selectedAnodeChemistries,
    selectedCathodeChemistries,
    // Filter state
    selectedChemistries,
    selectedFormFactors,
    selectedManufacturers,
    selectedTechnologies,
    // Actions
    toggleFilter,
    toggleSection,
    updateRange,
    yearRange
} from '../../state/filterStore.js'
import { basis, domain } from '../../state/globalSignals.js'
import {
    IconActivity,
    IconBattery,
    IconBox,
    IconCalendar,
    IconClock,
    IconCpu,
    IconFactory,
    IconFlask,
    IconMaximize,
    IconMinimize,
    IconSearch,
    IconSettings,
    IconZap
} from '../shared/Icons.jsx'

// Filter definitions/descriptions for tooltips
const FILTER_DEFINITIONS = {
  format: "The filter operates on the format of a cell.",
  energy: "The filter operates on the C/10 energy of the cells. Batemo measured the C/10 energy and capacity by discharging the cell at an ambient temperature of 25°C from 100% with a constant current of 0.1C until reaching the minimum nominal voltage.",
  power: "The filter operates on the peak power of the cells. The peak power is the power that the cell can supply for 5 minutes. The cell is discharged from 100% SOC at an ambient temperature of 25°C with a constant current until it reaches either the lower voltage limit or the maximum nominal surface temperature after 5 minutes. For cells that reach the maximum surface temperature, the measured current is taken directly as the peak current. For cells that do not reach the maximum surface temperature after 5 minutes because they reach the lower voltage limit first, the measured current is multiplied by a correction factor that estimates the current that would have heated the cell to the maximum surface temperature within 5 minutes.",
  capacity: "The filter operates on the C/10 capacity of the cells. Batemo measured the C/10 capacity by discharging the cell at an ambient temperature of 25°C from 100% with a constant current of 0.1C until reaching the minimum nominal voltage.",
  current: "The filter operates on the peak current of the cells. The peak current is the current that the cell can supply for 5 minutes without exceeding temperature or voltage limits.",
  batchVariation: "The filter operates on the standard deviation of the capacity of the cells, which were measured at Batemo. When cells are measured in the Batemo Lab, they are first pre-cycled for 10 cycles. To estimate the Cell-to-Cell Variation, capacity of at least 4 cells are compared at the end of pre-cycling. The given score value represents the standard deviation of the capacity measurements on the measured cells. Lower values indicate better consistency. Note, that this number does not give information about the batch-to-batch variation of cells (e.g. between production batches from different facilities). Also, the given number represents the variation in the cells selected for characterization at Batemo. A manufacturer might be able to provide carefully selected cells with lower variability.",
  aging: "The filter operates on the capacity loss of cells during the first equivalent full cycles. As formation is a costly process step, for some cells formation might not be completely finished, so that cells show an initial capacity fade at the begin of operation. In that case, a cell quickly looses some of its capacity and thus after only a few cycles it ends up at a capacity smaller than expected. In order to estimate the severity of this effect, the Post-Formation Loss Score represents the capacity loss of a cell during Batemo's validation measurements at an ambient temperature of 25°C. Lower values indicate less Post-Formation losses.",
  technology: "The filter operates on the type of Ion, that shuttles (intercalate/de-intercalate) between the cathode and anode through the electrolyte.",
  chemistry: "Filter by cell chemistry, organized into Cathode (e.g., NMC, NCA, LFP) and Anode (e.g., Graphite, Silicon, LTO) active materials. Materials are identified via EDX analysis after cell opening. As distinction by EDX is often ambiguous, Aluminum-doped NMC (NMCA) is labelled as NMC. In case of blended electrodes, the filter operates on the active material with the largest volume fraction. 'Analysis Ongoing' indicates cells not yet opened.",
  manufacturer: "The filter operates on the name of the manufacturer as printed on the cell or given by the supplier.",
  year: "The filter operates on the year, at which the cell was first measured in the Batemo lab. As Batemo tries to integrate new cells as early as possible, this date correlates well with the release date of the cell."
}

/**
 * Range Slider Component with dual handles and dynamic faceted search support
 * @param {object} props.stats - Statistics from filterMetadata (min, max, distribution) - static/initial
 * @param {object} props.dynamicStats - Dynamic stats based on cross-filtered cells (min, max, hasData)
 * @param {Array} props.dynamicDistribution - Dynamic histogram distribution from cross-filtered cells
 */
function RangeSlider({ id, label, min: propMin = 0, max: propMax = 100, step, unit, value, onChange, presets, showHistogram, stats, dynamicStats, dynamicDistribution }) {
  // Static range for slider boundaries (full possible range from all cells)
  const staticMin = stats?.min ?? propMin
  const staticMax = stats?.max ?? propMax

  // Dynamic range based on cross-filtered cells (changes when other filters applied)
  // Only use dynamic values if there's actual data (hasData === true)
  // Fall back to static values when dynamic data is not available or has no data
  const hasDynamicData = dynamicStats?.hasData === true
  const dynamicMin = hasDynamicData ? dynamicStats.min : staticMin
  const dynamicMax = hasDynamicData ? dynamicStats.max : staticMax

  // Use dynamic min/max for the slider when available, but ensure valid range
  // If dynamic values are same (no range), use static values
  const min = (dynamicMin < dynamicMax) ? dynamicMin : staticMin
  const max = (dynamicMin < dynamicMax) ? dynamicMax : staticMax

  // Dynamic distribution for histogram (based on current cross-filters)
  // Falls back to static distribution if no dynamic data
  const distribution = dynamicDistribution?.length > 0 ? dynamicDistribution : (stats?.distribution || [])

  // Check if there's any data available after cross-filtering
  const hasData = dynamicStats?.hasData !== false

  const [localMin, setLocalMin] = useState(value.min)
  const [localMax, setLocalMax] = useState(value.max)
  const [isDragging, setIsDragging] = useState(false)

  // Update local state when prop changes (external updates like resetAllFilters)
  useEffect(() => {
    setLocalMin(value.min)
    setLocalMax(value.max)
  }, [value.min, value.max])

  // Auto-adjust VISUAL state when dynamic range changes (other filters applied/removed)
  //
  // IMPORTANT: This effect NEVER calls onChange (store update).
  // Only direct user interactions (slider drag, number input, reset button)
  // are allowed to update the store. This prevents auto-adjusted ranges from
  // becoming active filters that incorrectly reduce the result set.
  //
  // The slider visually follows the dynamic range so the user sees the
  // available data distribution, but the actual filter in the store is
  // only set when the user explicitly interacts with this slider.
  useEffect(() => {
    if (hasDynamicData && dynamicMin < dynamicMax) {
      // Start from the store value (value.min/max) rather than stale localMin/localMax
      // This ensures preset-set ranges survive the auto-adjust effect.
      let newLocalMin = value.min
      let newLocalMax = value.max
      let needsUpdate = false

      // Shrink: clamp inward if local values exceed dynamic range
      if (newLocalMin < dynamicMin) {
        newLocalMin = dynamicMin
        needsUpdate = true
      }
      if (newLocalMax > dynamicMax) {
        newLocalMax = dynamicMax
        needsUpdate = true
      }

      // Expand: when the store value covers the full range (user hasn't manually
      // restricted this slider), expand local visuals to follow dynamic range.
      // This ensures sliders reset visually when categorical filters are removed.
      if (newLocalMin > dynamicMin && value.min <= dynamicMin) {
        newLocalMin = dynamicMin
        needsUpdate = true
      }
      if (newLocalMax < dynamicMax && value.max >= dynamicMax) {
        newLocalMax = dynamicMax
        needsUpdate = true
      }

      if (newLocalMin > newLocalMax) {
        newLocalMin = dynamicMin
        newLocalMax = dynamicMax
        needsUpdate = true
      }

      if (needsUpdate) {
        setLocalMin(newLocalMin)
        setLocalMax(newLocalMax)
      }
    }
  }, [hasDynamicData, dynamicMin, dynamicMax, value.min, value.max])

  // Prevent scroll during drag
  useEffect(() => {
    if (isDragging) {
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = ''
      }
    }
  }, [isDragging])

  const handleMinChange = (e) => {
    let newMin = parseFloat(e.target.value)
    // Clamp: min slider cannot exceed max slider
    if (newMin > localMax) {
      newMin = localMax
      // Force the slider visual position to match the clamped value
      e.target.value = newMin
    }
    setLocalMin(newMin)
  }

  const handleMaxChange = (e) => {
    let newMax = parseFloat(e.target.value)
    // Clamp: max slider cannot go below min slider
    if (newMax < localMin) {
      newMax = localMin
      // Force the slider visual position to match the clamped value
      e.target.value = newMax
    }
    setLocalMax(newMax)
  }

  const handleMinMouseUp = () => {
    setIsDragging(false)
    onChange({ min: localMin, max: localMax })
  }

  const handleMaxMouseUp = () => {
    setIsDragging(false)
    onChange({ min: localMin, max: localMax })
  }

  const handleMouseDown = () => {
    setIsDragging(true)
  }

  const handleMinInputChange = (e) => {
    const newMin = parseFloat(e.target.value)
    if (!isNaN(newMin) && newMin >= min && newMin <= localMax) {
      setLocalMin(newMin)
      onChange({ min: newMin, max: localMax })
    }
  }

  const handleMaxInputChange = (e) => {
    const newMax = parseFloat(e.target.value)
    if (!isNaN(newMax) && newMax <= max && newMax >= localMin) {
      setLocalMax(newMax)
      onChange({ min: localMin, max: newMax })
    }
  }

  const handleReset = () => {
    setLocalMin(min)
    setLocalMax(max)
    onChange({ min, max })
  }

  const handlePreset = (presetMin, presetMax) => {
    setLocalMin(presetMin)
    setLocalMax(presetMax)
    onChange({ min: presetMin, max: presetMax })
  }

  // Calculate progress bar position
  const range = max - min
  const leftPercent = ((localMin - min) / range) * 100
  const widthPercent = ((localMax - localMin) / range) * 100

  return (
    <div className="filter-range-slider">
      <div className="range-header">
        <span className="range-label">{label}</span>
        <button
          className="range-reset"
          onClick={handleReset}
          title="Reset range"
        >
          ↻
        </button>
      </div>

      {showHistogram && distribution.length > 0 && (
        <div className="range-histogram">
          <svg viewBox="0 0 100 30" preserveAspectRatio="none">
            {/* Generate histogram bars from distribution data */}
            {(() => {
              const maxCount = Math.max(...distribution.map(b => b.count))
              // If all counts are equal or zero, show a bell curve shape instead
              if (maxCount === 0 || distribution.every(b => b.count === maxCount)) {
                // Generate a gaussian-like distribution for visual appeal
                const bellCurve = distribution.map((_, i) => {
                  const center = distribution.length / 2
                  const x = i - center
                  const sigma = distribution.length / 4
                  return Math.exp(-(x * x) / (2 * sigma * sigma))
                })
                const maxBell = Math.max(...bellCurve)
                return distribution.map((bin, i) => {
                  const barWidth = 100 / distribution.length
                  const x = (i / distribution.length) * 100
                  const height = (bellCurve[i] / maxBell) * 25 + 2 // Min height 2
                  const y = 30 - height
                  return (
                    <rect
                      key={i}
                      x={x}
                      y={y}
                      width={barWidth}
                      height={height}
                      fill="rgba(234, 91, 12, 0.3)"
                      stroke="none"
                    />
                  )
                })
              }
              // Normal histogram with actual distribution
              return distribution.map((bin, i) => {
                const barWidth = 100 / distribution.length
                const x = (i / distribution.length) * 100
                const height = (bin.count / maxCount) * 30
                const y = 30 - height
                return (
                  <rect
                    key={i}
                    x={x}
                    y={y}
                    width={barWidth}
                    height={height}
                    fill="rgba(234, 91, 12, 0.3)"
                    stroke="none"
                  />
                )
              })
            })()}
          </svg>
        </div>
      )}
      {showHistogram && distribution.length === 0 && (
        <div className="range-histogram">
          <svg viewBox="0 0 100 30" preserveAspectRatio="none">
            <path d="M0,30 L5,28 L10,25 L15,20 L20,15 L25,10 L30,8 L35,12 L40,18 L45,22 L50,25 L55,27 L60,29 L65,30 L70,30 L75,30 L80,30 L85,30 L90,30 L95,30 L100,30"
                  fill="rgba(234, 91, 12, 0.2)" />
          </svg>
        </div>
      )}

      <div className="range-track">
        <div
          className="range-progress"
          style={{
            left: `${leftPercent}%`,
            width: `${widthPercent}%`
          }}
        />
        <input
          type="range"
          className="range-min"
          min={min}
          max={max}
          step={step}
          value={localMin}
          onInput={handleMinChange}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMinMouseUp}
          onTouchStart={handleMouseDown}
          onTouchEnd={handleMinMouseUp}
        />
        <input
          type="range"
          className="range-max"
          min={min}
          max={max}
          step={step}
          value={localMax}
          onInput={handleMaxChange}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMaxMouseUp}
          onTouchStart={handleMouseDown}
          onTouchEnd={handleMaxMouseUp}
        />
      </div>

      <div className="range-values">
        <input
          type="number"
          className="range-input-min"
          min={min}
          max={localMax}
          step={step}
          value={localMin}
          onChange={handleMinInputChange}
        />
        <span className="range-separator">–</span>
        <input
          type="number"
          className="range-input-max"
          min={localMin}
          max={max}
          step={step}
          value={localMax}
          onChange={handleMaxInputChange}
        />
        <span className="range-unit">{unit}</span>
      </div>

      {presets && presets.length > 0 && (
        <div className="range-presets">
          {presets.map(preset => (
            <button
              key={preset.label}
              className="range-preset"
              onClick={() => handlePreset(preset.min, preset.max)}
            >
              {preset.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Multi-Select Checkbox List with Dynamic Counts (Faceted Search)
 * @param {Array} options - Filter options from metadata { value, label, count (static) }
 * @param {Array} selectedValues - Currently selected values
 * @param {function} onChange - Toggle callback
 * @param {boolean} searchable - Enable search within options
 * @param {Map} dynamicCounts - Dynamic counts from cross-filter computation (optional)
 */
function MultiSelectFilter({ options, selectedValues, onChange, searchable, dynamicCounts }) {
  const [searchTerm, setSearchTerm] = useState('')

  // Merge options with dynamic counts
  const optionsWithDynamicCounts = useMemo(() => {
    if (!dynamicCounts || dynamicCounts.size === 0) {
      return options
    }

    return options.map(opt => ({
      ...opt,
      dynamicCount: dynamicCounts.get(opt.value) ?? 0,
      isDisabled: (dynamicCounts.get(opt.value) ?? 0) === 0 && !selectedValues.includes(opt.value)
    }))
  }, [options, dynamicCounts, selectedValues])

  const filteredOptions = optionsWithDynamicCounts.filter(opt =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Sort alphabetically (case-insensitive, numeric-aware so 4680 < 18650).
  // Options keep their place when selected or disabled, so the list never
  // reshuffles under the cursor.
  const sortedOptions = useMemo(() => {
    return [...filteredOptions].sort((a, b) =>
      (a.label || '').localeCompare(b.label || '', undefined, { numeric: true, sensitivity: 'base' })
    )
  }, [filteredOptions])

  const handleToggle = (value) => {
    onChange(value)
  }

  const handleSelectAll = () => {
    sortedOptions.forEach(opt => {
      if (!selectedValues.includes(opt.value) && !opt.isDisabled) {
        onChange(opt.value)
      }
    })
  }

  const handleSelectNone = () => {
    selectedValues.forEach(value => {
      if (sortedOptions.some(opt => opt.value === value)) {
        onChange(value)
      }
    })
  }

  return (
    <div className="filter-multi-select">
      {searchable && (
        <div className="filter-search">
          <span className="filter-search-icon"><IconSearch size={14} /></span>
          <input
            type="text"
            className="filter-search-input"
            placeholder="Search..."
            value={searchTerm}
            onInput={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      )}

      <div className="filter-options">
        {sortedOptions.map(option => {
          const isSelected = selectedValues.includes(option.value)
          const displayCount = option.dynamicCount ?? option.count
          const isDisabled = option.isDisabled && !isSelected

          return (
            <label
              key={option.value}
              className={`filter-option ${isDisabled ? 'filter-option--disabled' : ''}`}
            >
              <input
                type="checkbox"
                value={option.value}
                checked={isSelected}
                disabled={isDisabled}
                onChange={() => handleToggle(option.value)}
              />
              <span className="option-label">{option.label}</span>
              {displayCount !== undefined && (
                <span className={`option-count ${displayCount === 0 ? 'option-count--zero' : ''}`}>
                  ({displayCount})
                </span>
              )}
            </label>
          )
        })}
      </div>

      <div className="filter-actions">
        <button className="filter-select-all" onClick={handleSelectAll}>
          All
        </button>
        <button className="filter-select-none" onClick={handleSelectNone}>
          None
        </button>
      </div>
    </div>
  )
}

/**
 * Collapsible Filter Section with info tooltip
 * @param {string} title - Main title (e.g., "Energy")
 * @param {string} unit - Unit in parentheses (e.g., "(Wh)")
 * @param {string} infoText - Definition/description shown on hover
 */
function FilterSection({ id, title, unit, icon, children, alwaysExpanded = false, infoText }) {
  const expanded = isSectionExpanded(id)
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 })
  const iconRef = useRef(null)

  const handleToggle = () => {
    if (!alwaysExpanded) {
      toggleSection(id)
    }
  }

  const handleMouseEnter = () => {
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect()
      setTooltipPos({
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2
      })
      setShowTooltip(true)
    }
  }

  const handleMouseLeave = () => {
    setShowTooltip(false)
  }

  // Tooltip als Portal rendern, um overflow:hidden zu umgehen
  const tooltipPortal = showTooltip && infoText && createPortal(
    <div
      className="filter-info-tooltip-portal"
      style={{
        position: 'fixed',
        top: `${tooltipPos.top}px`,
        left: `${tooltipPos.left}px`,
        transform: 'translateX(-50%)',
        zIndex: 999999
      }}
    >
      {infoText}
    </div>,
    document.body
  )

  return (
    <div className={`filter-section ${expanded ? 'expanded' : 'collapsed'} ${alwaysExpanded ? 'always-expanded' : ''}`}>
      <div className="filter-header" onClick={handleToggle}>
        <div className="filter-header-left">
          {icon && <span className="filter-header-icon">{icon}</span>}
          <span className="filter-header-title">
            {title}
            {unit && <span className="filter-header-unit"> {unit}</span>}
          </span>
          {infoText && (
            <span
              ref={iconRef}
              className="filter-info-icon"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onClick={(e) => e.stopPropagation()}
            >
              ?
            </span>
          )}
        </div>
        {!alwaysExpanded && (
          <button className="filter-collapse-btn">▼</button>
        )}
      </div>
      <div className="filter-content">
        {children}
      </div>
      {tooltipPortal}
    </div>
  )
}

/**
 * Preset List Component
 */
function PresetList({ current, onSelect }) {
  const presets = [
    { id: 'high-energy', name: 'High Energy', icon: '⚡' },
    { id: 'high-power', name: 'High Power', icon: '🚀' },
    { id: 'ev-grade', name: 'EV Grade', icon: '🚗' },
    { id: 'energy-storage', name: 'Energy Storage', icon: '🔋' },
    { id: 'consumer', name: 'Consumer Electronics', icon: '📱' }
  ]

  return (
    <div className="filter-presets">
      {presets.map(preset => (
        <button
          key={preset.id}
          className={`preset-item ${current === preset.id ? 'active' : ''}`}
          onClick={() => onSelect(preset.id)}
        >
          <span className="preset-icon">{preset.icon}</span>
          <span className="preset-name">{preset.name}</span>
        </button>
      ))}
      <button className="preset-save">
        <span className="preset-icon">💾</span>
        Save Current Filters
      </button>
    </div>
  )
}

// Metadata keys whose stats back a range slider (see initFilterRangesFromMetadata)
const METADATA_RANGE_KEYS = ['energy', 'power', 'capacity', 'current', 'voltage', 'mass', 'volume', 'batchVariation', 'aging', 'year']

/**
 * Main FilterPanel Component
 */
export default function FilterPanel() {
  const collapsed = filterPanelCollapsed.value
  const [metadata, setMetadata] = useState(null)
  const [loading, setLoading] = useState(true)

  // Load filter metadata on mount AND when basis/domain changes
  useEffect(() => {
    let mounted = true

    async function loadMetadata() {
      try {
        setLoading(true)
        console.log('[FilterPanel] Loading metadata for basis:', basis.value, 'domain:', domain.value)
        const data = await getFilterMetadata()
        if (mounted) {
          setMetadata(data)
          console.log('[FilterPanel] Loaded metadata:', data)
          console.log('[FilterPanel] DEBUG cathodeChemistries:', data?.cathodeChemistries)
          console.log('[FilterPanel] DEBUG anodeChemistries:', data?.anodeChemistries)
          console.log('[FilterPanel] DEBUG ongoingAnalysis:', data?.ongoingAnalysis)

          // Initialize filter ranges from actual data
          initFilterRangesFromMetadata(data)
        }
      } catch (error) {
        console.error('[FilterPanel] Failed to load metadata:', error)
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    loadMetadata()

    return () => {
      mounted = false
    }
  }, [basis.value, domain.value]) // Re-calculate when basis or domain changes

  // getFilterMetadata() serves the IndexedDB copy at once and revalidates it in the
  // background; take the refreshed option lists (manufacturers, chemistries, ...) when they
  // land. The range stats stay those the range signals were initialised from:
  // initFilterRangesFromMetadata() would reset every range the user or an Ion command set
  // (and degrade Ion's attribution), so fresh ranges apply on the next basis/domain reload.
  useEffect(() => onFilterMetadataUpdate((fresh) => {
    setMetadata(prev => {
      if (!prev) return fresh
      const next = { ...fresh }
      for (const key of METADATA_RANGE_KEYS) next[key] = prev[key]
      return next
    })
  }), [])

  const handleTogglePanel = () => {
    filterPanelCollapsed.value = !collapsed
  }

  // Get basis/domain-aware labels and units
  const getEnergyLabel = () => {
    const currentBasis = basis.value
    if (currentBasis === 'abs') return { label: 'Energy (Wh)', unit: 'Wh' }
    if (currentBasis === 'grav') return { label: 'Energy (Wh/kg)', unit: 'Wh/kg' }
    if (currentBasis === 'vol') return { label: 'Energy (Wh/L)', unit: 'Wh/L' }
    return { label: 'Energy', unit: '' }
  }

  const getPowerLabel = () => {
    const currentBasis = basis.value
    if (currentBasis === 'abs') return { label: 'Power (W)', unit: 'W' }
    if (currentBasis === 'grav') return { label: 'Power (W/kg)', unit: 'W/kg' }
    if (currentBasis === 'vol') return { label: 'Power (W/L)', unit: 'W/L' }
    return { label: 'Power', unit: '' }
  }

  const getCapacityLabel = () => {
    const currentBasis = basis.value
    if (currentBasis === 'abs') return { label: 'Capacity (Ah)', unit: 'Ah' }
    if (currentBasis === 'grav') return { label: 'Capacity (Ah/kg)', unit: 'Ah/kg' }
    if (currentBasis === 'vol') return { label: 'Capacity (Ah/L)', unit: 'Ah/L' }
    return { label: 'Capacity', unit: '' }
  }

  const getCurrentLabel = () => {
    const currentBasis = basis.value
    if (currentBasis === 'abs') return { label: 'Current (A)', unit: 'A' }
    if (currentBasis === 'grav') return { label: 'Current (A/kg)', unit: 'A/kg' }
    if (currentBasis === 'vol') return { label: 'Current (A/L)', unit: 'A/L' }
    return { label: 'Current', unit: '' }
  }

  // Dynamic options from metadata
  // Helper function for safe sorting - handles undefined/null labels
  const safeSort = (a, b) => {
    const aLabel = a?.label || ''
    const bLabel = b?.label || ''
    return aLabel.localeCompare(bLabel)
  }

  // Chemistry options split into cathode, anode, and ongoing
  const cathodeChemistryOptions = (metadata?.cathodeChemistries?.map(c => ({
    value: c.name,
    label: c.name,
    count: c.count,
    group: 'cathode'
  })).filter(c => c.label) || []).sort(safeSort)

  const anodeChemistryOptions = (metadata?.anodeChemistries?.map(c => ({
    value: c.name,
    label: c.name,
    count: c.count,
    group: 'anode'
  })).filter(c => c.label) || []).sort(safeSort)

  const ongoingAnalysisOptions = (metadata?.ongoingAnalysis?.map(c => ({
    value: c.name,
    label: c.name,
    count: c.count,
    group: 'ongoing'
  })).filter(c => c.label) || [])

  // Fallback to combined chemistries if split data not available
  const chemistryOptions = (metadata?.chemistries?.map(c => ({
    value: c.name,
    label: c.name,
    count: c.count
  })).filter(c => c.label) || []).sort(safeSort)

  const manufacturerOptions = (metadata?.manufacturers?.map(m => ({
    value: m.name,
    label: m.name,
    count: m.count
  })).filter(m => m.label) || []).sort(safeSort)

  const countryOptions = (metadata?.countries?.map(c => ({
    value: c.name,
    label: c.name,
    count: c.count
  })).filter(c => c.label) || []).sort(safeSort)

  const formFactorOptions = (metadata?.formFactors?.map(f => ({
    value: f.name,
    label: f.name,
    count: f.count
  })).filter(f => f.label) || []).sort(safeSort)

  const technologyOptions = (metadata?.technologies?.map(t => ({
    value: t.name,
    label: t.name,
    count: t.count
  })).filter(t => t.label) || []).sort(safeSort)

  console.log('[FilterPanel] chemistryOptions:', chemistryOptions)
  console.log('[FilterPanel] manufacturerOptions:', manufacturerOptions)
  console.log('[FilterPanel] formFactorOptions:', formFactorOptions)
  console.log('[FilterPanel] technologyOptions:', technologyOptions)

  return (
    <div className={`cells-filter-panel ${collapsed ? 'collapsed' : ''}`} data-tour="explorer-filter-panel">
      {!collapsed && (
        <div className="filter-panel-header">
          {/* Global Quick Search - Live search, Enter to save as tag */}
          <div className="filter-search">
            <span className="filter-search-icon"><IconSearch size={14} /></span>
            <input
              type="text"
              className="filter-search-input"
              placeholder="Search cells... (Enter to save)"
              value={searchQuery.value}
              onInput={(e) => searchQuery.value = e.target.value}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchQuery.value.trim()) {
                  const term = searchQuery.value.trim()
                  if (!savedSearchTerms.value.includes(term)) {
                    savedSearchTerms.value = [...savedSearchTerms.value, term]
                  }
                  searchQuery.value = '' // Clear input for next search
                }
              }}
            />
            {searchQuery.value && (
              <button
                className="filter-search-clear"
                onClick={() => searchQuery.value = ''}
                title="Clear search"
                type="button"
              >
                ×
              </button>
            )}
          </div>

          {/* Saved Search Terms */}
          {savedSearchTerms.value.length > 0 && (
            <div className="saved-search-terms">
              {savedSearchTerms.value.map((term, idx) => (
                <div key={idx} className="search-term-tag">
                  <span className="search-term-text">{term}</span>
                  <button
                    className="search-term-remove"
                    onClick={() => {
                      savedSearchTerms.value = savedSearchTerms.value.filter((_, i) => i !== idx)
                    }}
                    title="Remove search term"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Collapse/Expand All Controls */}
          <div className="filter-panel-actions">
            <button
              className="filter-action-btn"
              onClick={collapseAllSections}
              title="Collapse All Sections"
            >
              <IconMinimize size={14} />
              <span>Collapse All</span>
            </button>
            <button
              className="filter-action-btn"
              onClick={expandAllSections}
              title="Expand All Sections"
            >
              <IconMaximize size={14} />
              <span>Expand All</span>
            </button>
          </div>
        </div>
      )}

      {!collapsed && loading && (
        <div className="filter-panel-loading">
          <div className="loading-spinner__animation">
            <div className="loading-spinner__ring"></div>
            <div className="loading-spinner__core"></div>
            <div className="loading-spinner__icon">
              <IconSettings size={20} />
            </div>
          </div>
          <p className="loading-spinner__text">Loading Filters</p>
        </div>
      )}

      {!collapsed && !loading && (
        <>
          {/* Scrollable content area */}
          <div className="filter-panel-content">

          {/* 1. Format Filter */}
          <FilterSection id="format" title="Format" icon={<IconBox size={16} />} infoText={FILTER_DEFINITIONS.format}>
            <MultiSelectFilter
              options={formFactorOptions}
              selectedValues={selectedFormFactors.value}
              onChange={(value) => toggleFilter('form-factor', value)}
              dynamicCounts={dynamicFilterCounts.value.formFactors}
            />
          </FilterSection>

          {/* DOMAIN-AWARE FILTERS: Show Energy/Power when domain=energy-power */}
          {domain.value === 'energy-power' && (
            <>
              {/* 2. Energy Range */}
              <FilterSection id="energy" title="Energy" unit={`(${getEnergyLabel().unit})`} icon={<IconZap size={16} />} infoText={FILTER_DEFINITIONS.energy}>
                <RangeSlider
                  id="energy"
                  label={getEnergyLabel().label}
                  step={5}
                  unit={getEnergyLabel().unit}
                  value={energyRange.value}
                  onChange={(range) => updateRange('energy', range)}
                  showHistogram
                  stats={metadata?.energy?.[basis.value]}
                  dynamicStats={dynamicRangeData.value.ranges?.energy}
                  dynamicDistribution={dynamicRangeData.value.histograms?.energy}
                />
              </FilterSection>

              {/* 3. Power Range */}
              <FilterSection id="power" title="Power" unit={`(${getPowerLabel().unit})`} icon={<IconZap size={16} />} infoText={FILTER_DEFINITIONS.power}>
                <RangeSlider
                  id="power"
                  label={getPowerLabel().label}
                  step={50}
                  unit={getPowerLabel().unit}
                  value={powerRange.value}
                  onChange={(range) => updateRange('power', range)}
                  showHistogram
                  stats={metadata?.power?.[basis.value]}
                  dynamicStats={dynamicRangeData.value.ranges?.power}
                  dynamicDistribution={dynamicRangeData.value.histograms?.power}
                />
              </FilterSection>
            </>
          )}

          {/* DOMAIN-AWARE FILTERS: Show Capacity/Current when domain=capacity-current */}
          {domain.value === 'capacity-current' && (
            <>
              {/* 2. Capacity Range */}
              <FilterSection id="capacity" title="Capacity" unit={`(${getCapacityLabel().unit})`} icon={<IconBattery size={16} />} infoText={FILTER_DEFINITIONS.capacity}>
                <RangeSlider
                  id="capacity"
                  label={getCapacityLabel().label}
                  step={0.1}
                  unit={getCapacityLabel().unit}
                  value={capacityRange.value}
                  onChange={(range) => updateRange('capacity', range)}
                  showHistogram
                  stats={metadata?.capacity?.[basis.value]}
                  dynamicStats={dynamicRangeData.value.ranges?.capacity}
                  dynamicDistribution={dynamicRangeData.value.histograms?.capacity}
                />
              </FilterSection>

              {/* 3. Current Range */}
              <FilterSection id="current" title="Current" unit={`(${getCurrentLabel().unit})`} icon={<IconZap size={16} />} infoText={FILTER_DEFINITIONS.current}>
                <RangeSlider
                  id="current"
                  label={getCurrentLabel().label}
                  step={5}
                  unit={getCurrentLabel().unit}
                  value={currentRange.value}
                  onChange={(range) => updateRange('current', range)}
                  showHistogram
                  stats={metadata?.current?.[basis.value]}
                  dynamicStats={dynamicRangeData.value.ranges?.current}
                  dynamicDistribution={dynamicRangeData.value.histograms?.current}
                />
              </FilterSection>
            </>
          )}

          {/* 4. Cell-to-Cell Variation - CAP_spread_std_rel in % */}
          <FilterSection id="batchVariation" title="Cell-to-Cell Variation" unit="(%)" icon={<IconActivity size={16} />} infoText={FILTER_DEFINITIONS.batchVariation}>
            <RangeSlider
              id="batchVariation"
              label="Cell-to-Cell Variation"
              step={0.1}
              unit="%"
              value={batchVariationRange.value}
              onChange={(range) => updateRange('batchVariation', range)}
              showHistogram
              stats={metadata?.batchVariation}
              dynamicStats={dynamicRangeData.value.ranges?.batchVariation}
              dynamicDistribution={dynamicRangeData.value.histograms?.batchVariation}
            />
          </FilterSection>

          {/* 5. Post-Formation Loss - CAP_loss_rel in % */}
          <FilterSection id="aging" title="Post-Formation Loss" unit="(%)" icon={<IconClock size={16} />} infoText={FILTER_DEFINITIONS.aging}>
            <RangeSlider
              id="aging"
              label="Post-Formation Loss"
              step={0.1}
              unit="%"
              value={agingRange.value}
              onChange={(range) => updateRange('aging', range)}
              showHistogram
              stats={metadata?.aging}
              dynamicStats={dynamicRangeData.value.ranges?.aging}
              dynamicDistribution={dynamicRangeData.value.histograms?.aging}
            />
          </FilterSection>

          {/* 6. Technology Filter */}
          <FilterSection id="technology" title="Technology" icon={<IconCpu size={16} />} infoText={FILTER_DEFINITIONS.technology}>
            <MultiSelectFilter
              options={technologyOptions}
              selectedValues={selectedTechnologies.value}
              onChange={(value) => toggleFilter('technology', value)}
              dynamicCounts={dynamicFilterCounts.value.technologies}
            />
          </FilterSection>

          {/* 7. Chemistry Filter - Split into Cathode, Anode, and Ongoing (AND logic between groups) */}
          <FilterSection id="chemistry" title="Chemistry" icon={<IconFlask size={16} />} infoText={FILTER_DEFINITIONS.chemistry}>
            {/* Cathode Chemistry (cathode_material_group) */}
            {cathodeChemistryOptions.length > 0 && (
              <div className="filter-subgroup">
                <div className="filter-subgroup-label">Cathode</div>
                <MultiSelectFilter
                  options={cathodeChemistryOptions}
                  selectedValues={selectedCathodeChemistries.value}
                  onChange={(value) => toggleFilter('cathode-chemistry', value)}
                  dynamicCounts={dynamicFilterCounts.value.cathodeChemistries}
                />
              </div>
            )}

            {/* Anode Chemistry (anode_material_group) */}
            {anodeChemistryOptions.length > 0 && (
              <div className="filter-subgroup">
                <div className="filter-subgroup-label">Anode</div>
                <MultiSelectFilter
                  options={anodeChemistryOptions}
                  selectedValues={selectedAnodeChemistries.value}
                  onChange={(value) => toggleFilter('anode-chemistry', value)}
                  dynamicCounts={dynamicFilterCounts.value.anodeChemistries}
                />
              </div>
            )}

            {/* Analysis Ongoing - use legacy chemistry signal */}
            {ongoingAnalysisOptions.length > 0 && (
              <div className="filter-subgroup">
                <MultiSelectFilter
                  options={ongoingAnalysisOptions}
                  selectedValues={selectedChemistries.value}
                  onChange={(value) => toggleFilter('chemistry', value)}
                />
              </div>
            )}

            {/* Fallback if no split data */}
            {cathodeChemistryOptions.length === 0 && anodeChemistryOptions.length === 0 && (
              <MultiSelectFilter
                options={chemistryOptions}
                selectedValues={selectedChemistries.value}
                onChange={(value) => toggleFilter('chemistry', value)}
              />
            )}
          </FilterSection>

          {/* 8. Manufacturer Filter */}
          <FilterSection id="manufacturer" title="Manufacturer" icon={<IconFactory size={16} />} infoText={FILTER_DEFINITIONS.manufacturer}>
            <MultiSelectFilter
              options={manufacturerOptions}
              selectedValues={selectedManufacturers.value}
              onChange={(value) => toggleFilter('manufacturer', value)}
              maxHeight={400}
              dynamicCounts={dynamicFilterCounts.value.manufacturers}
            />
          </FilterSection>

          {/* 9. Year Range */}
          <FilterSection id="year" title="Year" icon={<IconCalendar size={16} />} infoText={FILTER_DEFINITIONS.year}>
            <RangeSlider
              id="year"
              label="Year"
              min={1990}
              max={new Date().getFullYear()}
              step={1}
              unit=""
              value={yearRange.value}
              onChange={(range) => updateRange('year', range)}
              showHistogram
              stats={metadata?.year}
              dynamicStats={dynamicRangeData.value.ranges?.year}
              dynamicDistribution={dynamicRangeData.value.histograms?.year}
            />
          </FilterSection>

          </div>
          {/* End of scrollable content */}

          {/* Footer with Reset */}
          <div className="filter-panel-footer">
            <button
              className="filter-reset-btn"
              onClick={resetAllFilters}
              disabled={!hasActiveFilters.value}
            >
              Reset All Filters {activeFilterCount.value > 0 && `(${activeFilterCount.value})`}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
